import { useId, useState } from 'react'
import { t } from '@stokr/components-library'
import { Section, SectionSkeleton } from 'components/sto/Section'
import { CardTabs, TabCard, TabCardBody, TabPanel } from 'components/sto/Tabs'
import { useChartDenomination, useProductChart } from 'hooks/sto/useProductChart'
import { useStoProject } from 'hooks/sto/useStoProject'
import { useNavComposition } from 'hooks/sto/useNavComposition'
import { ProjectTypes } from 'constants/enums'
import type { ProductChartView } from 'models/productChart'
import { pickNavComposition } from 'models/navComposition'
import { FundPerformance } from './FundPerformance'
import { MiningNotePerformance } from './MiningNotePerformance'
import { StockPerformance } from './StockPerformance'
import { NavPanel } from './NavPanel'
import { useStoTrack } from 'hooks/sto/useStoTracking'

type PerformanceTab = 'chart' | 'issuance' | 'nav'

/** One chart component per chart kind. A new kind fails to compile here until it has one. */
const ChartForKind = ({ view, tab }: { view: ProductChartView; tab: PerformanceTab }) => {
  const { kind } = view.chart
  switch (kind) {
    case 'fund':
      return <FundPerformance {...view} />
    case 'stock':
      return <StockPerformance {...view} />
    case 'miningNote':
      return <MiningNotePerformance {...view} view={tab === 'issuance' ? 'issuance' : 'mined'} />
    default: {
      const unhandled: never = kind
      return unhandled
    }
  }
}

/**
 * The product's charts in a tabbed card. The first tab reads "Underlying Performance" for
 * equity, which tracks another stock, "BTC Mined" for mining notes and "Performance" for funds;
 * funds with a NAV composition add "NAV composition". The composition comes from content, SSR'd:
 * only the chart data loads in the browser, as a skeleton.
 */
export const Performance = () => {
  const state = useProductChart()
  const compositions = useNavComposition()
  const { project } = useStoProject()
  const panelId = useId()
  // None picked: the first tab, the issuance amount for a running note.
  const [picked, setPicked] = useState<PerformanceTab>()
  const trackSto = useStoTrack()
  const symbol = project?.tokenSymbol?.trim() ?? ''
  // The composition follows the hero's currency switch, as the chart does (Goldstream: XAUt first).
  const denomination = useChartDenomination(state.status === 'ready' ? state.data.chart : undefined)
  const composition = pickNavComposition(compositions, denomination?.currency)
  const isFund = project?.type === ProjectTypes.FUND
  // Mining notes name their tabs by what they show: total value first while the note runs, then BTC mined.
  const isNote = project?.type === ProjectTypes.TRANCHES
  const hasIssuance = state.status === 'ready' && Boolean(state.data.chart.note?.issuance?.length)
  // A note without mining data (PKH2) has its issuance only: no "BTC mined" tab.
  const hasMined = !isNote || state.status !== 'ready' || state.data.chart.denominations[0].points.length > 0

  const tabs = [
    ...(hasIssuance ? [{ id: 'issuance' as const, label: t('stoPage.performance.issuanceTab') }] : []),
    ...(state.status !== 'hidden' && hasMined
      ? [
          {
            id: 'chart' as const,
            label: isNote
              ? t('stoPage.performance.minedTab')
              : isFund
                ? t('stoPage.performance.fundTab')
                : t('stoPage.performance.title'),
          },
        ]
      : []),
    // No composition, no tab: the fund's NAV over time is the chart's NAV view.
    ...(isFund && composition ? [{ id: 'nav' as const, label: t('stoPage.performance.nav.tab') }] : []),
  ]
  const active = tabs.find((tab) => tab.id === picked) ?? tabs[0]

  return (
    <Section
      anchor="performance"
      state={active ? { status: 'ready', data: active } : { status: 'hidden' }}
      variant="plain">
      {(tab) => {
        return (
          <TabCard>
            <CardTabs
              tabs={tabs}
              activeId={tab.id}
              onSelect={(id) => {
                if (id !== tab.id) trackSto('sto_performance_tab_selected', { tab: id })
                setPicked(id)
              }}
              panelId={panelId}
              label={t('stoPage.performance.viewLabel')}
              fit
              dataCy="sto-performance-tab"
            />
            <TabPanel activeKey={tab.id} id={panelId}>
              <TabCardBody>
                {tab.id === 'nav' && composition ? (
                  <NavPanel months={composition.months} currency={composition.currency} symbol={symbol} />
                ) : state.status !== 'ready' ? (
                  <SectionSkeleton rows={8} />
                ) : (
                  <ChartForKind view={state.data} tab={tab.id} />
                )}
              </TabCardBody>
            </TabPanel>
          </TabCard>
        )
      }}
    </Section>
  )
}
